/**
 * ★ 定点白名单 —— **全项目唯一**允许做「除法 / 取整 / 费率换算」的位置。
 *
 * 依据：规格 **§22.1**（金额整数分 · `PRICE_SCALE=10000` · 汇率 `1e-4` · 全链路禁浮点）
 *      · 架构 **§6.1**（金额传输类型 = 整数 `number` + Zod `.int()` 上下界）
 *      · `DR-023` D3 · `P域PRD` **AC-P1.1 / AC-P1.2 / AC-P1.3**
 *
 * ## 🔴 为什么需要这个文件
 * 「禁浮点」≠「禁除法」—— 规格 §22.1 自身就要求 `round()` 至分与 `PRICE_SCALE` / `1e-4` 运算。
 * 所以规则不能写成"禁止一切除法"，而必须写成：
 * **除法 / 取整 / 费率换算只许出现在本文件**（ESLint `no-restricted-syntax` 在别处一律阻断）。
 *
 * ## 🔴 本文件遵守的三条不变量
 * 1. **只在放大后的整数域计算，只在最后一步取整** —— 绝不出现"先除出小数再四舍五入"。
 * 2. **每个中间乘积先断言在安全整数内**（`Number.isSafeInteger`）—— 溢出立即抛错，不静默丢精度。
 * 3. **四舍五入 = 远离零**（`+1.5 → 2` · `-1.5 → -2`）—— 对称，避免 `Math.round(-0.5) === -0`
 *    在账务上制造不对称观感。⚠️ 该策略是**实施期定夺项**，见 PR 说明。
 *
 * ## 使用示例
 * ```ts
 * import { mulRate, priceToCents, allocate, toCents } from '@jiaying/shared';
 * const cny = mulRate(usd, rate);            // 折本币
 * const amt = priceToCents(qty, price);      // 投资金额
 * const parts = allocate(total, weights);    // 分摊（Σ 严格守恒）
 * ```
 */

import type { Cents, PriceE4, QtyE4, RateE4, RatioE4 } from '../types/brand.js';

// ─────────────────────────────── 常量 ───────────────────────────────

/** 1 元 = 100 分（规格 §22.1：金额整数「分」） */
export const UNIT_SCALE = 100;

/** 价格 / 汇率放大倍数：万分之一（规格 §22.1 · 精度 R6） */
export const PRICE_SCALE = 10000;
export const RATE_SCALE = 10000;

/** 分 → 元 的显示比例（与 `UNIT_SCALE` 同源，禁止各端各写一份 100） */
export const CENTS_PER_YUAN = UNIT_SCALE;

/**
 * 「百分数 → E4 比率」的标度因子 = **100**（`DR-044`）。
 *
 * 🔴 **不要**用 `UNIT_SCALE` / `CENTS_PER_YUAN` 顶替 —— 它们是"元→分"的因子，
 * 数值同为 100 纯属巧合；混用会让下一个人以为"利率与金额同标度"。
 * 关系式只有一个：`E4 = 百分数 × 100`（`10000` = 100%）。
 */
export const E4_PER_PERCENT = 100;

/**
 * `priceToCents` 的除数：数量(×10⁴) × 价格(×10⁴) = 元 × 10⁸，再 ×100 得「分」⇒ 除以 10⁶。
 * 与规格 §22 附录的 `金额(分) = round(数量 × 价格 / 1_000_000)` 逐字对应。
 */
const PRICE_QTY_DIVISOR = 1_000_000;

/** 安全整数上界（架构 §6.1：≈9e15 分 ≈ 9e13 元，远超家庭账本量级） */
export const MAX_SAFE_CENTS = Number.MAX_SAFE_INTEGER;

// ─────────────────────────── 内部：断言 ───────────────────────────

/**
 * 断言 `n` 是安全整数（`|n| ≤ 2^53−1`）。
 * 🔴 用于**每个乘法之后**：整数相乘一旦越界，`number` 会静默丢精度 —— 那是账目对不上的根源。
 */
export function assertSafeInteger(n: number, what = '数值'): void {
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`${what} 超出安全整数范围（|x| ≤ 2^53−1）：${n}`);
  }
}

// ───────────────────── 内部：唯一取整实现 ─────────────────────

/**
 * 四舍五入除法（`d` 必须为正整数），**远离零**舍入。
 *
 * 实现要点（避免 `n / d` 的浮点舍入误差）：
 * 先用 `Math.trunc` 求粗商，**再用整数乘法还原精确余数**并校正商；
 * 只有余数的整数比较（`|r| × 2 ≥ d`）参与判半，故不依赖浮点除法的末位。
 */
function roundDivBy(n: number, d: number): number {
  assertSafeInteger(n, '定点运算的被除数');
  if (!Number.isInteger(d) || d <= 0) {
    throw new RangeError(`定点运算的除数必须是正整数：${d}`);
  }

  let q = Math.trunc(n / d);
  let r = n - q * d; // 精确余数（q、r 均为整数且未越界）

  // 校正粗商（防 n/d 的浮点舍入把商带偏 ±1）
  while (r >= d) {
    q += 1;
    r -= d;
  }
  while (r <= -d) {
    q -= 1;
    r += d;
  }

  // 四舍五入 · 远离零
  if (Math.abs(r) * 2 >= d) {
    q += n < 0 ? -1 : 1;
  }
  assertSafeInteger(q, '定点运算的结果');
  return q;
}

// ─────────────────── 内部：浮点输入的唯一收口 ───────────────────

/**
 * 把**浮点数**四舍五入为整数（远离零）。
 *
 * 🔴 只用于「外部浮点输入」的收口（`yuanToCents` / `roundToCents`）——
 * **定点域内部不得使用它**，否则等于把浮点误差放回内核。
 */
function roundFloatHalfAway(x: number): number {
  const rounded = x < 0 ? -Math.round(-x) : Math.round(x);
  // 归一 -0 → +0：金额域不应出现负零（`Object.is(-0, 0) === false`，会污染相等判断与序列化）
  return rounded === 0 ? 0 : rounded;
}

// ─────────────────────────── 构造 / 舍入 ───────────────────────────

/**
 * 把已在「分」域的整数标为 `Cents`（**不做任何换算**）。
 * 🔴 这是进入金额域的正规入口；`valueInCents` 必须是整数，否则抛错。
 */
export function toCents(valueInCents: number): Cents {
  if (!Number.isInteger(valueInCents)) {
    throw new RangeError(`金额必须是整数「分」，收到小数：${valueInCents}`);
  }
  assertSafeInteger(valueInCents, '金额');
  return valueInCents as Cents;
}

/**
 * 把「分」域的一个数四舍五入为整数分 —— **本项目唯一的 `round` 出口**。
 * 接受小数入参（用于浮点结果收口）；四舍五入 = **远离零**。
 */
export function roundToCents(valueInCents: number): Cents {
  if (!Number.isFinite(valueInCents)) {
    throw new RangeError(`金额必须是有限数：${valueInCents}`);
  }
  const rounded = roundFloatHalfAway(valueInCents);
  assertSafeInteger(rounded, '金额（分）');
  return rounded as Cents;
}

/**
 * 「元」→「分」· **number 版**。
 * ⚠️ 仅用于**展示值回填 / 粗转换**：入参是 `number`，因此**受二进制浮点表示影响**
 * （例：`1.005` 的 double 实际值 ≈ `1.00499999999999989`，会得到 100 分而非 101 分）。
 * 🔴 表单录入 / 导入 / 对账等**需要精确**的场合，请用 `yuanTextToCents()`。
 */
export function yuanToCents(yuan: number): Cents {
  if (!Number.isFinite(yuan)) {
    throw new RangeError(`金额必须是有限数：${yuan}`);
  }
  return roundToCents(yuan * UNIT_SCALE);
}

/**
 * 「元」→「分」· **十进制精确版**（推荐用于表单录入 / 导入 / 对账）。
 *
 * 🔴 与 `yuanToCents(number)` 的关键差别：**按十进制字面量解析，不经二进制浮点**。
 * 例：`yuanTextToCents('1.005')` → `101` 分；而 `yuanToCents(1.005)` → `100` 分。
 *
 * 接受 `123` / `123.4` / `123.45` / `+0.1` / `-3.005` 形式的字面量；
 * 超过 2 位小数按「远离零」舍入（第 3 位 ≥ 5 进 1）。
 */
export function yuanTextToCents(text: string): Cents {
  const m = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(text.trim());
  if (!m) {
    throw new RangeError(`金额文本格式非法（应为十进制字面量）：${JSON.stringify(text)}`);
  }
  const sign = m[1] === '-' ? -1 : 1;
  const yuanInt = Number(m[2]);
  assertSafeInteger(yuanInt, '金额整数部分');

  const frac = (m[3] ?? '').padEnd(3, '0').slice(0, 3);
  const cents = yuanInt * UNIT_SCALE + Number(frac.slice(0, 2)) + (Number(frac[2]) >= 5 ? 1 : 0);
  assertSafeInteger(cents, '金额（分）');

  // 避免产生 -0（0.004 元应为 0 分，而非 -0）
  return (sign < 0 && cents !== 0 ? -cents : cents) as Cents;
}

// ───────────────────────────── 换算 ─────────────────────────────

// ── E4 比率 ↔ 百分数文本（`DR-044`）──
//
// 🔴 为什么放在定点白名单里：百分数与 `E4` 的换算是「×100 / ÷100」，
// 写在别处就得用 `/`（被 ESLint 块 A 拦住）或 `Math.trunc`（同）。放这里 = 唯一实现处。
//
// ⚠️ **两者的标度关系**：`E4` 的 `10000` = 100% ⇒ **`E4` = 百分数 × 100**
// （3.36% → 336 · 3% → 300 · 0.5% → 50）。这也正是 `DR-044` D3 说"与 v1 的
// `annual_rate_bps` 数值完全相同"的原因（bps = 万分之一 = E4）。

/**
 * 百分数文本 → `E4` 比率（`DR-044`：年利率录入）。
 *
 * 接受 `3` / `3.36` / `0.5`（**最多两位小数** —— 因为 `E4` 只能精确到百分数的两位小数；
 * 三位小数如 `3.575%` 会落到 `E4` 的非整点 `357.5`）。
 *
 * 🔴 **按十进制字面量解析、纯整数运算**（与 `yuanTextToCents` 同手法）——
 * 不经二进制浮点，`3.36` 必然得 `336`。
 *
 * @throws RangeError 格式非法（含负数、三位以上小数、非数字）
 */
export function percentTextToRateE4(text: string): RateE4 {
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!m) {
    throw new RangeError(
      `百分数文本格式非法（应为非负十进制、最多两位小数）：${JSON.stringify(text)}`,
    );
  }
  const whole = Number(m[1]);
  assertSafeInteger(whole, '百分数整数部分');
  const frac = (m[2] ?? '').padEnd(2, '0');
  const rate = whole * E4_PER_PERCENT + Number(frac);
  assertSafeInteger(rate, 'E4 比率');
  return rate as RateE4;
}

/**
 * `E4` 比率 → 百分数文本（`DR-044`：编辑态回填）。
 *
 * `336` → `"3.36"` · `300` → `"3"` · `50` → `"0.50"`。
 *
 * 🔴 **非零小数恒两位** —— 不能"省掉末尾的 0"：`308` 必须写成 `"3.08"` 而非 `"3.8"`
 * （后者是**另一个利率**，且回填进输入框后再提交会变成 `380`）。整数比值才不带小数点。
 *
 * 🔴 **整数除法**（`E4` 是整数标度 ⇒ 商必然是整数），不是浮点除法。
 */
export function rateE4ToPercentText(rateE4: RateE4): string {
  const rate = rateE4 as number;
  assertSafeInteger(rate, 'E4 比率');
  const whole = Math.trunc(rate / E4_PER_PERCENT);
  const frac = rate % E4_PER_PERCENT;
  return frac === 0 ? String(whole) : `${String(whole)}.${String(frac).padStart(2, '0')}`;
}

/**
 * 折本币：`金额(分) × 汇率(万分之一)` → 本币金额(分)，**四舍五入至分**。
 * 与规格精度 R6「`round(原币额 × 汇率)` 至整数分」逐字对应（此处汇率以 `1e-4` 整数承载）。
 */
export function mulRate(amount: Cents, rate: RateE4): Cents {
  return scaleByE4(amount, rate as number, '金额 × 汇率');
}

/**
 * 按 **E4 比率**缩放金额：`round(amount × ratioE4 ÷ 10000)`。
 *
 * 🔴 **为什么需要它**：`mulRate` 的名字来自"汇率"，而汇率只是"E4 标度比率"的一种。
 * 非汇率场景（如预算三档建议的「**收入 30%**」—— `BudgetThreshold.IncomeSuggestE4`）
 * 若直接调 `mulRate`，调用处要么写 `as unknown as RateE4`（把品牌类型当装饰，
 * 恰好废掉它"防串用"的用途），要么各自 `divideExact` 再拼一遍（第二份实现）。
 * ⇒ 给出一个**名副其实**的入口，实现仍是同一处（见 {@link scaleByE4}）。
 */
export function mulRatioE4(amount: Cents, ratioE4: RatioE4): Cents {
  return scaleByE4(amount, ratioE4 as number, '金额 × 比率');
}

/**
 * 「金额 × E4 标度因子」的**唯一实现** —— `mulRate` 与 `mulRatioE4` 共用。
 *
 * 除法与取整**只允许出现在本文件**（规格 §22.1 / `DR-023` D3）。
 */
function scaleByE4(amount: Cents, scaleE4: number, label: string): Cents {
  const scaled = (amount as number) * scaleE4;
  assertSafeInteger(scaled, label);
  return roundDivBy(scaled, RATE_SCALE) as Cents;
}

/**
 * 上游给的**小数汇率** → **万分之一整数汇率**（`rateE4`）。
 *
 * 🔴 **为什么必须在本文件**：`Math.round(x * 10000)` 属**定点转换**，与 `roundToCents` 同类 ——
 * 规格 §22.1 / `DR-023` D3 要求"除法与取整只出现在定点白名单内"。
 * ESLint 的 `no-restricted-syntax` 会在调用处**直接报错**，那是**设计意图**（不是障碍）：
 * 金额与汇率一旦能在任意文件里随手取整，"对不上的账"就再也查不出来。
 *
 * 用途：汇率域从 Frankfurter 拿到的是**小数**（实测恰 4 位，如 `6.7045`），
 * 入库前必须转成万分之一整数（`67045`）。
 *
 * ⚠️ 入参是 `number`（来自 JSON）⇒ 受二进制浮点影响；上游只给 4 位小数时**无损**
 * （`6.7045 × 10000 = 67045.00000000001` ⇒ 取整仍为 `67045`）。
 *
 * @throws 非正 / 非有限值 —— 🔴 **不返回 0 兜底**：静默的 0 会让折算把外币算成 0 元
 */
export function rateE4FromNumber(value: number): RateE4 {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`汇率必须是正的有限数：${value}`);
  }
  const scaled = roundFloatHalfAway(value * RATE_SCALE);
  assertSafeInteger(scaled, '汇率（万分之一）');
  return scaled as RateE4;
}

/**
 * 汇率取倒数：`1 / rate` → 万分之一整数。
 *
 * 用途：折算方向与汇率表的登记方向不一致时（跨币种资产折本币）。
 * 🔴 **精度说明**：结果仍为万分之一标度，**取倒数本身会引入一次舍入**
 * （如 `rate = 3` 会溢出异常）⇒ 调用方应**优先查正向汇率**，
 * 仅在确实没有正向登记时才用本函数（`composeNetWorth` 即此策略）。
 */
export function invertRate(rate: RateE4): RateE4 {
  const r = rate as number;
  if (!Number.isFinite(r) || r <= 0) {
    throw new RangeError(`汇率必须为正的有限数：${rate}`);
  }
  const scaled = RATE_SCALE * RATE_SCALE;
  assertSafeInteger(scaled, '汇率平方');
  return roundDivBy(scaled, r) as RateE4;
}

/**
 * 投资金额：`数量(×10⁴) × 价格(万分之元/份)` → 金额(分)，**四舍五入至分**。
 * 对应规格 §22 附录「金额(分) = `round(数量 × 价格 / 1_000_000)`」。
 */
export function priceToCents(quantity: QtyE4, price: PriceE4): Cents {
  const scaled = (quantity as number) * (price as number);
  assertSafeInteger(scaled, '数量 × 价格');
  return roundDivBy(scaled, PRICE_QTY_DIVISOR) as Cents;
}

// ───────────────────────────── 占比 ─────────────────────────────

/**
 * 占比：`part / total` → **「万分之一」整数**（`3333` = 33.33%）。
 *
 * 用途：报表「分类占比」· 预算「使用率」· 目标「完成度」——
 * 这些本质都是除法；若不在此收敛，业务层会被 `no-restricted-syntax` 拦下。
 *
 * 约定：
 * - `total === 0` → 返回 **0**。分母为零时内核**不猜语义**（是"无数据"还是"无穷大"由调用方定）；
 *   调用方要显示「暂无数据」，判 `total === 0` 即可。
 * - `part > total`（超额完成 / 超支）→ **不截断**，可返回 > 10000；是否封顶由展示层决定。
 *   （依据：规格 §23「不截断」在本项目多处仍有效，见 §24 之二十的适用范围说明。）
 * - `total` 为负（如净资产为负）→ 符号随 `total` 翻转，保持 `part / total` 的代数语义。
 */
export function ratioE4(part: Cents, total: Cents): RatioE4 {
  return ratioE4Of(part as number, total as number);
}

/**
 * **通用比值**：`part / total` → 万分之一整数 —— 用于**非金额**的比
 * （预算「时间进度」= `elapsedDays / totalDays`、日均可花的天数比等）。
 *
 * 🔴 **语义与 {@link ratioE4} 完全一致**（同一实现），只是入参不要求 `Cents` brand ——
 * 因为天数 / 计数不是金额，硬套 brand 会逼调用方做假转换（`toCents(days)`）。
 * ⚠️ **金额比请仍用 `ratioE4`**（保住 brand 的类型防线）。
 */
export function ratioE4Of(part: number, total: number): RatioE4 {
  const totalNum = total;
  if (totalNum === 0) return 0 as RatioE4;

  const scaled = part * RATE_SCALE;
  assertSafeInteger(scaled, '占比分子（part × 10000）');

  const magnitude = roundDivBy(scaled, Math.abs(totalNum));
  return (totalNum < 0 ? -magnitude : magnitude) as RatioE4;
}

// ─────────────────────────── 通用整除 ───────────────────────────

/**
 * **通用整除出口** —— 用于非金额场景的整数除法，如
 * `毫秒差 ÷ 86400000` 求天数（预算窗口的 `totalDays` / `elapsedDays`）。
 *
 * 🔴 **仅在「被除数确实是除数的整数倍」时结果才精确**（如两个 UTC 零点之差）；
 * 否则按「远离零」四舍五入返回 —— 调用方须自行确认可整除性。
 * ⚠️ 它不返回 `Cents`：结果不是金额，硬套 brand 会污染类型语义。
 */
export function divideExact(n: number, d: number): number {
  if (d === 0) {
    throw new RangeError('除数不得为 0');
  }
  assertSafeInteger(n, '被除数');
  return roundDivBy(n, d);
}

/**
 * **向下取整的整数除法**（`floor(n / d)`，`d > 0`）。
 *
 * 用途：`月均攒速 = 已攒 ÷ 经过月数`（I 域目标节奏）—— 这种"速率"必须**向下取整**：
 * 向上取会让预计达成日**提前**，用户按它排预算就会差一截。
 *
 * 🔴 **为什么不直接用 `Math.floor(n / d)`**：`/` 与 `Math.floor` 在 `money.ts` 之外被 ESLint
 * 全面封死（`AC-P1.2` · `DR-023` D3）。要么在此提供具名出口，要么每个调用点写
 * `eslint-disable` 并各自解释一遍 —— 后者会让"禁浮点"纪律在第三次豁免后名存实亡。
 *
 * ⚠️ 除数必须为**正**整数（负除数下 `floor` 与「截断」的分歧会让结果难以预期，故直接拒绝）。
 */
export function floorDiv(n: number, d: number): number {
  const q = truncDiv(n, d);
  // 精确余数：q 由截断得到 ⇒ r 的符号与 n 相同
  const r = n - q * d;
  return r < 0 ? q - 1 : q;
}

/**
 * **向上取整的整数除法**（`ceil(n / d)`，`d > 0`）。
 *
 * 用途：`还需要几个月 = 缺口 ÷ 月均攒速` —— 差 1 分钱也得再攒一个月，
 * 所以这里必须**向上**取整（与 {@link floorDiv} 相反的理由）。
 */
export function ceilDiv(n: number, d: number): number {
  const q = truncDiv(n, d);
  const r = n - q * d;
  return r > 0 ? q + 1 : q;
}

/** `floorDiv` / `ceilDiv` 的公共前半段：截断商 + 入参校验（不对外导出） */
function truncDiv(n: number, d: number): number {
  assertSafeInteger(n, '整数除法的被除数');
  if (!Number.isInteger(d) || d <= 0) {
    throw new RangeError(`整数除法的除数必须是正整数：${String(d)}`);
  }
  // 截断商（`roundDivBy` 的校正循环同款做法：先粗算，再按精确余数校正）
  let q = Math.trunc(n / d);
  let r = n - q * d;
  while (r >= d) {
    q += 1;
    r -= d;
  }
  while (r <= -d) {
    q -= 1;
    r += d;
  }
  return q;
}

// ─────────────────────────── 展示格式化 ───────────────────────────

/**
 * 「分」→ **无符号无千分位的十进制元文本**（如 `123456` → `"1234.56"`）。
 *
 * ## 🔴 为什么它必须在这里（而不是各端各写一遍）
 * `@typescript-eslint` 的禁浮点规则把 `/` 与 `Math.trunc` **全项目封死**、只留本文件
 * （规则文案原文就写着「金额格式化请走展示层统一函数」）——
 * ⇒ 那么"那个统一函数"就得**真的存在**，否则每个需要展示金额的地方都会：
 * 要么各自 `eslint-disable`（纪律从此失效），要么用字符串切片绕过去（更糟）。
 *
 * ## 三条约定
 * 1. **不含货币符号、不含千分位** —— 符号（`¥` / `$`）与分组是**展示层**决定，
 *    一并做进来就会与 `DR-004`「金额一律显示原币 + 币种符号」的币种来源打架；
 * 2. **恒保留 2 位小数**（`"5.00"` 而不是 `"5"`）—— 账目文本里丢掉 `.00` 会让人以为被截断；
 * 3. **负号在最前**（`"-12.34"`），且 `-0` 归一成 `"0.00"`（金额域不出现负零）。
 */
export function centsToYuanText(cents: Cents): string {
  const n = cents as number;
  assertSafeInteger(n, '金额（分）');
  const sign = n < 0 ? '-' : '';
  const abs = n < 0 ? -n : n;
  const frac = abs % UNIT_SCALE;
  const yuan = divideExact(abs - frac, UNIT_SCALE);
  return `${sign}${yuan}.${String(frac).padStart(2, '0')}`;
}

/**
 * 「万分之一标度整数」→ 定点十进制文本（**恒 4 位小数**）。
 *
 * 用于投资域的价格（元/份）与份额：
 * | 输入 | 输出 | 含义 |
 * |---|---|---|
 * | `10300` | `"1.0300"` | 净值 ¥1.03 |
 * | `12345` | `"1.2345"` | |
 * | `9708738` | `"970.8738"` | 970.8738 份 |
 * | `0` | `"0.0000"` | |
 *
 * ## 🔴 为什么也必须在这里
 * 与 {@link centsToYuanText} 同因：端侧 `no-restricted-syntax` 把 `/`、`toFixed`、
 * `Math.round` 全封死 ⇒ 若没有这个函数，每个显示价格的地方都得自己 `eslint-disable`
 * 或用字符串切片绕过（后者更容易写错，且没人测）。
 *
 * ## 约定
 * - **不含货币符号**（价格符号由展示层拼 —— `DR-036`）；
 * - **恒 4 位** —— 净值 `1.03` 显示成 `"1.03"` 会与 `1.0301` 看不出差别，
 *   而投资界的报价习惯就是 4 位；
 * - 负号在最前，`-0` 归一成 `"0.0000"`（金额域不出现负零）。
 */
export function e4ToText(value: number): string {
  assertSafeInteger(value, '万分之一标度值');
  const sign = value < 0 ? '-' : '';
  const abs = value < 0 ? -value : value;
  const frac = abs % PRICE_SCALE;
  const whole = divideExact(abs - frac, PRICE_SCALE);
  return `${sign}${String(whole)}.${String(frac).padStart(4, '0')}`;
}

/**
 * 「万分之一标度整数」→ **去掉末尾 0** 的十进制文本。
 *
 * 份额用这个而不是 {@link e4ToText}：`1000000`（100 份）显示成 `"100.0000"` 是噪音，
 * 而 `"100"` 一眼看得出「整 100 份」。
 *
 * | 输入 | 输出 |
 * |---|---|
 * | `1000000` | `"100"` |
 * | `123456` | `"12.3456"` |
 * | `5000` | `"0.5"` |
 */
export function e4ToTrimmedText(value: number): string {
  const full = e4ToText(value);
  if (!full.includes('.')) return full;
  // 先削尾零，再削可能剩下的孤零零小数点（`"12.0000"` → `"12."` → `"12"`）
  return full.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * 十进制文本 → **万分之一标度整数**（最多 4 位小数）—— 与 {@link e4ToText} 互逆。
 *
 * `"1.03"` → `10300` · `"100"` → `1000000` · `"0.0001"` → `1`。
 *
 * 🔴 **按十进制字面量解析、纯整数运算**（与 `percentTextToRateE4` / `yuanTextToCents`
 * 同一手法）—— 不经二进制浮点，`1.03` 必然得 `10300`（用 `parseFloat` 会得到
 * `10299.999999999998` 再取整成 `10299`）。
 *
 * ⚠️ **允许负号**（解析层保持中立）；「价格/份额必须为正」是业务约束，
 * 由调用方或契约层判 —— 在这里顺手拒掉会让将来的退款/调整场景无处安放。
 *
 * @throws RangeError 格式非法（超过 4 位小数 / 空串 / 非数字）
 */
export function e4TextToInt(text: string): number {
  const m = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(text.trim());
  if (!m) {
    throw new RangeError(
      `定点文本格式非法（应为十进制、最多 4 位小数）：${JSON.stringify(text)}`,
    );
  }
  const whole = Number(m[2]);
  assertSafeInteger(whole, '定点文本整数部分');
  const frac = (m[3] ?? '').padEnd(4, '0');
  const scaled = whole * PRICE_SCALE + Number(frac);
  assertSafeInteger(scaled, '定点文本标度值');
  return m[1] === '-' ? -scaled : scaled;
}

// ─────────────────────────── 贷款计息（`DR-010` 决策二 · 批 80） ───────────────────────────
//
// 🔴 **为什么放本文件**：逐期计息与等额本息月供都是「金额 × 费率 ÷ 标度」的定点运算，
// 按规格 §22.1 / `DR-023` D3 只能落在定点白名单内。业务形状（计划表 / 重排 / 退化判定）
// 在 `nucleus/loan.ts`，本文件只出**算术原语**。
//
// ⚠️ **素材与等价化说明**：v1 `packages/server/src/common/amortization.ts`（只读取证）
// 用浮点（`Math.pow` + `Math.round`）计算月供与利息；本实现改**整数域精确有理数**
// —— 见 {@link annuityPaymentCents} 的推导。逐期利息的两处算法在常见参数下一致，
// 但不再依赖二进制浮点（同一输入恒得同一结果，黄金向量可断言）。

/**
 * 年利率（`E4`）→ 月利率的**除法分母**：`月利率 = annualRateE4 ÷ 120000`。
 *
 * 推导：月利率 = 年利率 ÷ 12 期 = `(annualRateE4 ÷ 10000) ÷ 12` ⇒ 合并分母 `10000 × 12`。
 * 🔴 **不要**写成「先除 10000 再除 12」两步 —— 两步各舍入一次，误差翻倍；一步到位只舍一次。
 */
export const LOAN_MONTHLY_RATE_DIVISOR = 120_000;

/**
 * 逐期计息的**唯一实现**：`round(余额 × 年利率E4 ÷ 120000)`。
 *
 * 用途：贷款计划表每期利息（等额本息与等额本金**同一处**计息 —— 铁律 24）。
 * 🔴 余额为 0 时返回 0（结清后不计息），余额为负是调用方 bug ⇒ 抛错不静默。
 */
export function monthlyInterestCents(balanceCents: Cents, annualRateE4: RateE4): Cents {
  const bal = balanceCents as number;
  const rate = annualRateE4 as number;
  assertSafeInteger(bal, '计息余额');
  if (bal < 0) {
    throw new RangeError(`计息余额不得为负（负余额应先取绝对值并说明口径）：${bal}`);
  }
  if (!Number.isInteger(rate) || rate < 0) {
    throw new RangeError(`年利率非法（应为非负的万分之一整数）：${rate}`);
  }
  if (bal === 0) return 0 as Cents;
  return divideExact(bal * rate, LOAN_MONTHLY_RATE_DIVISOR) as Cents;
}

/**
 * **等额本息月供**：`round(P·r ÷ (1 − (1+r)^−n))`，r = 月利率。
 *
 * ## 🔴 整数域精确推导（为什么没有浮点也能算）
 *
 * `r = annualRateE4 / 120000` 是**精确有理数**（记 `A = 120000 + annualRateE4`、`B = 120000`，
 * 则 `1 + r = A/B`），于是
 *
 * ```
 * (1+r)^n = A^n / B^n
 * 月供 = P·r·(1+r)^n / ((1+r)^n − 1) = P·annualRateE4·A^n / (B·(A^n − B^n))
 * ```
 *
 * 分子分母都是**纯整数**（BigInt），一次除法 + 一次四舍五入（远离零）即得月供，
 * **全程无浮点、无幂级数近似**。`terms` 上限 1200 期（`AccountPolicy.TermsMax`），
 * BigInt 幂运算量级可忽略。
 *
 * ⚠️ `rate = 0` 时公式分母为 0 ⇒ 特判为均分（`divideExact(P, n)`，末期由调用方吸收尾差）。
 */
export function annuityPaymentCents(principalCents: Cents, annualRateE4: RateE4, terms: number): Cents {
  const p = principalCents as number;
  const rate = annualRateE4 as number;
  assertSafeInteger(p, '贷款本金');
  if (p <= 0) {
    throw new RangeError(`贷款本金必须为正：${p}`);
  }
  if (!Number.isInteger(rate) || rate < 0) {
    throw new RangeError(`年利率非法（应为非负的万分之一整数）：${rate}`);
  }
  if (!Number.isInteger(terms) || terms <= 0) {
    throw new RangeError(`期数非法（应为正整数）：${terms}`);
  }
  if (rate === 0) {
    return divideExact(p, terms) as Cents;
  }
  // 本文件在定点白名单内（eslint 块 A 的 ignores），BigInt 的 `/` 是**整除**、非浮点除法
  const B = 120_000n;
  const A = B + BigInt(rate);
  const An = A ** BigInt(terms);
  const Bn = B ** BigInt(terms);
  const num = BigInt(p) * BigInt(rate) * An;
  const den = B * (An - Bn);
  // BigInt `/` 向零截断 ⇒ 手工补「四舍五入·远离零」（num/den 恒为正）
  let q = num / den;
  if ((num % den) * 2n >= den) q += 1n;
  const out = Number(q);
  assertSafeInteger(out, '等额本息月供');
  return out as Cents;
}

// ─────────────────────────── 聚合 / 分摊 ───────────────────────────

/**
 * 金额求和。整数加法在安全整数内**精确**，故不需舍入；但仍逐次断言防越界。
 */
export function sumCents(list: readonly Cents[]): Cents {
  let total = 0;
  for (const c of list) {
    total += c as number;
    assertSafeInteger(total, '金额求和');
  }
  return total as Cents;
}

/**
 * **最大余数法**分摊：按权重把 `total` 分毫不差地拆成 `n` 份。
 *
 * 🔴 不变量：`sumCents(结果) === total`（**逐分恒等**）—— `DR-022` Q7「多分类合并报销按垫付比例自动分摊」的硬要求。
 *
 * 规则：
 * - 权重须为非负整数且 `Σweights > 0`；
 * - 每份先取**整除下界**，剩余若干「分」按**余数降序**分配；
 * - 余数相同则**按索引升序**取（保证同一输入永远得到同一结果 —— 幂等可复算）；
 * - `total` 为负（冲减场景）时按同一规则反向分配，仍满足 Σ 恒等。
 */
export function allocate(total: Cents, weights: readonly number[]): Cents[] {
  const n = weights.length;
  if (n === 0) return [];
  if (total === 0) return weights.map(() => 0 as Cents);

  const sumW = weights.reduce((a, b) => a + b, 0);
  if (!Number.isInteger(sumW) || sumW <= 0) {
    throw new RangeError(`分摊权重之和必须为正整数：${sumW}`);
  }
  for (const w of weights) {
    if (!Number.isInteger(w) || w < 0) {
      throw new RangeError(`分摊权重必须是非负整数：${w}`);
    }
  }

  const totalNum = total as number;
  // ① 放大到整数域的分子（乘权重）
  const numerators = weights.map((w, i) => {
    const v = totalNum * w;
    assertSafeInteger(v, `分摊分子[${i}]（total × weight）`);
    return v;
  });

  // ② 整除下界 + 精确余数
  const quotient = new Array<number>(n);
  const remainder = new Array<number>(n);
  let assigned = 0;
  for (let i = 0; i < n; i += 1) {
    const q = Math.trunc(numerators[i]! / sumW);
    const r = numerators[i]! - q * sumW;
    quotient[i] = q;
    remainder[i] = r;
    assigned += q;
  }

  // ③ 剩余「分」按 |余数| 降序、索引升序分配
  const leftover = Math.abs(totalNum - assigned);
  if (leftover > 0) {
    const order = remainder
      .map((r, i) => ({ mag: Math.abs(r), i }))
      .sort((a, b) => b.mag - a.mag || a.i - b.i);
    const step = totalNum > 0 ? 1 : -1;
    for (let k = 0; k < leftover; k += 1) {
      const target = order[k % n]!.i;
      quotient[target] = (quotient[target] ?? 0) + step;
    }
  }

  const result = quotient as Cents[];
  // ④ 自证（内部不变式，失败即实现有 bug）
  const check = sumCents(result) as number;
  if (check !== totalNum) {
    throw new Error(`allocate 内部错误：分摊结果之和 ${check} ≠ 原始金额 ${totalNum}`);
  }
  return result;
}

// ═══════════════════════ XIRR（年化内部收益率 · `DR-059` D4/D8） ═══════════════════════

/**
 * 一笔现金流（**时间点已折算成"距首笔现金流的天数"**）。
 *
 * 🔴 本函数**不做日期运算**：`tDays` 由调用方用 `daysBetween()` 算好再传 ——
 * `money.ts` 是纯定点算术层，一旦 import 日历工具就会与 `calendar.ts` 形成回环。
 */
export interface XirrFlow {
  /** 距**首笔现金流**的天数（`≥ 0`，首笔为 0） */
  tDays: number;
  /** 有符号金额（整数分）：流出为负、流入为正值 */
  amountCents: number;
}

/**
 * **年化内部收益率（XIRR）** —— 解 `Σ CFᵢ / (1+r)^(tᵢ/365) = 0`。
 *
 * ## 🔴 为什么允许浮点（全项目唯一的浮点迭代处）
 *
 * 这道方程**没有整数解法**：`(1+r)^(t/365)` 本身就是超越运算。其余定点纪律（禁 `/`）
 * 的目的是"金额不因浮点累加而丢分"，而本函数输出的**是比率不是金额**，且立即被
 * `roundDivBy` 收成 `pctE4` 整数 ⇒ 不会把浮点误差带进账里。
 *
 * 🔴 因此本函数放在 `MONEY_WHITELIST`（`packages/shared/src/nucleus/money.ts`）内部，
 * **不新建第二个白名单文件** —— 扩白名单等于给"再放第三处"开口子（`DR-059` D8）。
 *
 * ## 返回 `null` 的三种情形（**禁返回 0**，0% 是合法收益率）
 *
 * 1. 现金流不足 2 笔，或**全部同号**（没有"先投后收"就无从定义收益率）；
 * 2. 区间的 NPV 不变号 ⇒ 方程在 `(-100%, +100000%)` 内无解；
 * 3. 迭代出现 `NaN` / 非有限值。
 *
 * ## 算法
 *
 * 二分法（对标准"先负后正"现金流，NPV 关于 r 单调递减），区间 `(-99.9999%, 100000%)`，
 * 200 次迭代足以把区间收到 1e-10 量级；比 Newton 更稳（不会因导数接近 0 而飞出去）。
 *
 * @returns `pctE4` = 百分比 × 100（`16230` = 162.30%），与 `usageE4` 同一约定
 */
export function xirrPctE4(flows: readonly XirrFlow[]): number | null {
  if (flows.length < 2) return null;

  const hasNegative = flows.some((f) => f.amountCents < 0);
  const hasPositive = flows.some((f) => f.amountCents > 0);
  if (!hasNegative || !hasPositive) return null;

  const npv = (rate: number): number => {
    let sum = 0;
    for (const f of flows) {
      // 🔴 tDays = 0 的项指数为 0 ⇒ 恒等于原值（首笔不能被折现）
      const years = f.tDays / 365;
      sum += f.amountCents / Math.pow(1 + rate, years);
    }
    return sum;
  };

  // 下界取 -99.9999%（(1+r) 必须为正）；上界 100000%（1000 倍），不够再扩
  let lo = -0.999999;
  let hi = 1000;
  let flo = npv(lo);
  let fhi = npv(hi);
  if (!Number.isFinite(flo) || !Number.isFinite(fhi)) return null;

  // 同号 ⇒ 无解（扩上界 3 次仍同号就放弃，避免死循环）
  let expand = 0;
  while (flo * fhi > 0 && expand < 3) {
    hi *= 10;
    fhi = npv(hi);
    if (!Number.isFinite(fhi)) return null;
    expand += 1;
  }
  if (flo * fhi > 0) return null;

  let mid = 0;
  for (let i = 0; i < 200; i += 1) {
    mid = (lo + hi) / 2;
    const fmid = npv(mid);
    if (!Number.isFinite(fmid)) return null;
    if (fmid === 0) break;
    if (flo * fmid < 0) {
      hi = mid;
      fhi = fmid;
    } else {
      lo = mid;
      flo = fmid;
    }
  }

  const pct = mid * 100; // 百分比
  if (!Number.isFinite(pct)) return null;
  // pctE4 = 百分比 × E4_PER_PERCENT（走定点取整，把浮点误差收在最后一步）
  return roundDivBy(Math.round(pct * 1_000_000), 1_000_000 / E4_PER_PERCENT);
}
